import { useState, useContext, useEffect, useRef, useCallback, type ReactNode } from 'react';
import { Animate, useNavigate, ViewContext } from 'components/lib';
import Style from './shared/processSteps.tailwind';
import Button from '@mui/material/Button';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CircularProgress from '@mui/material/CircularProgress';
import CheckCircle from '@untitled-ui/icons-react/build/cjs/CheckCircle';
import PlayCircle from '@untitled-ui/icons-react/build/cjs/PlayCircle';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import ExitDialog from 'components/exit-dialog/ExitDialog';
import { SimulationContext, type SubSimulation } from 'contexts/SimulationContext';
import { GeodataContext } from 'contexts/GeodataContext';
import { GridContext } from 'contexts/GridContext';
import { ValidationContext } from 'contexts/ValidationContext.js';
import { LastAddedItemContext } from 'contexts/LastAddedItemContext.js';
import { CurrentProjectContext } from 'contexts/CurrentProjectContext';
import { RunContext } from 'contexts/RunContext';
import { validateNoiseRoadsForSimulationSubmit } from 'services/noiseRoadsValidation';
import { normalizeWindMeshDistanceByLength2 } from 'services/normalizeWindMeshDistance.js';
import { hydrateWindSubSimulationsFromGrid } from 'services/windParamsHydration';
import { getRunnableSimulationCount, getSimulationLimitMessage } from 'services/simulationUsage';
import { CheckList } from './shared/checklist';
import { CloseIcon } from 'components/icons';
import { STORAGE_LIMIT_REACHED_MESSAGE } from '../../services/storageLimit';
import type { SimulationNode } from 'types/project';
import { submitSimulationSet } from './shared/submitSimulationSet';
import { getApiErrorMessage } from 'api/apiClient';
import { isShadowSimulationDefined } from './shadow/shadowResidentialAreas';
import { SHADOW_NOT_DEFINED_MESSAGE } from 'constants/simulation';

const toFiniteNumberOrNull = (value: unknown) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

type LooseSubSimulation = SubSimulation & { [key: string]: unknown };

interface OnboardingView {
  key?: string;
  name: string;
  title?: ReactNode;
  hideDefaultHeader?: boolean;
  customHeader?: ReactNode;
  component?: ReactNode;
  hideNextButton?: boolean;
  fillAvailableSpace?: boolean;
  onBack?: () => void;
}

interface OnboardingProps {
  views: OnboardingView[];
  readOnly?: boolean;
  hideForwardNavigation?: boolean;
  preserveDraftState?: boolean;
  countsTowardUsage?: boolean;
  isGeodataGathered?: boolean;
  geodataId?: string | number;
  gridData?: {
    id?: string | number;
    projectId?: string | number;
    params?: { manualCenter?: { x?: number | string | null; y?: number | string | null } | null } | null;
  } | null;
  projectData?: {
    id?: string | number;
    simulationsAvailableCount?: number | null;
  } | null;
  storageLimitReached?: boolean;
  onExit?: () => void;
}

export function SimulationCreationFlow(props: OnboardingProps) {
  const navigate = useNavigate();
  const viewContext = useContext(ViewContext);
  const { setLastAddedItem } = useContext(LastAddedItemContext);
  const { grid, clearGridFormData } = useContext(GridContext);
  const { geodataParams, clearGeodataFormData } = useContext(GeodataContext);
  const { simulation, clearSimulationFormData } = useContext(SimulationContext);
  const { currentProject } = useContext(CurrentProjectContext);
  const { handleRunSimulation, setAllSimulations, allSubSimulations } = useContext(RunContext);
  const { isSimulationNoiseSourcesValid, isSimulationWindSimulationsValid, isSimulationNameValid } =
    useContext(ValidationContext);

  const [isGeodataGathered, setIsGeodataGathered] = useState(Boolean(props?.isGeodataGathered));
  const [isStorageLimitReachedState, setIsStorageLimitReachedState] = useState(Boolean(props?.storageLimitReached));
  const [hasInputFieldError, setHasInputFieldError] = useState(false);
  const [invalidStepsById, setInvalidStepsById] = useState<Record<string, boolean>>({});
  const [exitModalOpen, setExitModalOpen] = useState(false);
  const [shadowDefinitionDialogOpen, setShadowDefinitionDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const articleRef = useRef<HTMLElement | null>(null);
  const isReadOnly = Boolean(props?.readOnly);
  const hideForwardNavigation = Boolean(props?.hideForwardNavigation);
  const hash = window.location.hash;
  const [activeView, setActiveView] = useState(hash ? Number(hash.substring(1)) - 1 : 0);

  useEffect(() => {
    setIsGeodataGathered(Boolean(props?.isGeodataGathered));
  }, [props?.isGeodataGathered]);

  useEffect(() => {
    setIsStorageLimitReachedState(Boolean(props?.storageLimitReached));
  }, [props?.storageLimitReached]);

  const getRequestedSimulationCount = useCallback(() => {
    const hydratedSubSimulations = normalizeWindMeshDistanceByLength2(
      hydrateWindSubSimulationsFromGrid(
        (simulation?.subSimulations || []) as LooseSubSimulation[],
        props?.gridData ?? grid
      ),
      geodataParams?.lengthMid
    );

    return getRunnableSimulationCount(hydratedSubSimulations);
  }, [geodataParams?.lengthMid, grid, props?.gridData, simulation?.subSimulations]);

  const validateProjectEntityAvailability = useCallback(
    async (requiredCount = 1) => {
      if ((props?.countsTowardUsage ?? true) === false) {
        return true;
      }

      const remainingCount = toFiniteNumberOrNull(
        props?.projectData?.simulationsAvailableCount ?? currentProject?.simulationsAvailableCount
      );

      if (remainingCount === null) {
        return true;
      }

      if (remainingCount <= 0) {
        viewContext.notification.show('No simulations are available for this project.', 'error', true, 'toast');
        return false;
      }

      if (requiredCount > remainingCount) {
        viewContext.notification.show(
          `You are trying to run ${requiredCount} simulations, but only ${remainingCount} are available for this project.`,
          'error',
          true,
          'toast'
        );
        return false;
      }

      return true;
    },
    [
      currentProject?.simulationsAvailableCount,
      props?.countsTowardUsage,
      props?.projectData?.simulationsAvailableCount,
      viewContext.notification,
    ]
  );

  const submitSimulation = useCallback(async () => {
    const geodataId = props?.geodataId;

    if (!geodataId) {
      throw new Error('Missing geodata id');
    }
    return submitSimulationSet({
      geodataId: String(geodataId),
      simulation,
      gridData: (props?.gridData ?? grid) as { params?: Record<string, unknown> | null },
      lengthMid: geodataParams?.lengthMid,
      resolutionMid: geodataParams?.resolutionMid,
      countsTowardUsage: props?.countsTowardUsage ?? true,
      availableTargets: Array.isArray(geodataParams?.targets) ? geodataParams.targets : [],
    });
  }, [
    geodataParams?.lengthMid,
    geodataParams?.resolutionMid,
    geodataParams?.targets,
    grid,
    props?.countsTowardUsage,
    props?.geodataId,
    props?.gridData,
    simulation,
  ]);

  useEffect(() => {
    if (isReadOnly) {
      setHasInputFieldError(false);
      return;
    }

    const root = articleRef.current;
    if (!root || !props?.views?.length) {
      setHasInputFieldError(false);
      return;
    }

    const stepId = props?.views?.[activeView]?.key || `step-${activeView}`;

    const collectDomErrorState = () => {
      const hasMuiError = Boolean(root.querySelector('.MuiFormHelperText-root.Mui-error, .Mui-error'));
      const hasCustomError = Boolean(root.querySelector('[class*="custom-error-message"]'));
      const hasAriaInvalid = Boolean(root.querySelector('[aria-invalid="true"]'));

      const nativeInvalid = Array.from(root.querySelectorAll('input, select, textarea')).some((element: Element) => {
        const field = element as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
        if (!field || field.disabled) return false;
        if (typeof field.checkValidity !== 'function') return false;
        return !field.checkValidity();
      });

      return hasMuiError || hasCustomError || hasAriaInvalid || nativeInvalid;
    };

    const updateStepErrorState = () => {
      const hasError = collectDomErrorState();

      setHasInputFieldError(hasError);
      setInvalidStepsById((prev) => {
        const wasInvalid = Boolean(prev[stepId]);
        if (hasError) {
          if (wasInvalid) return prev;
          return { ...prev, [stepId]: true };
        }
        if (!wasInvalid) return prev;
        return { ...prev, [stepId]: false };
      });
    };

    updateStepErrorState();

    const observer = new MutationObserver(() => updateStepErrorState());
    observer.observe(root, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['class', 'aria-invalid'],
    });

    const handleInteraction = () => updateStepErrorState();
    root.addEventListener('input', handleInteraction, true);
    root.addEventListener('change', handleInteraction, true);
    root.addEventListener('blur', handleInteraction, true);

    return () => {
      observer.disconnect();
      root.removeEventListener('input', handleInteraction, true);
      root.removeEventListener('change', handleInteraction, true);
      root.removeEventListener('blur', handleInteraction, true);
    };
  }, [activeView, isReadOnly, props?.views]);

  const checkNoiseRoadsBeforeSimulationSubmit = useCallback(async () => {
    const result = await validateNoiseRoadsForSimulationSubmit({
      simulation: simulation as unknown as Parameters<typeof validateNoiseRoadsForSimulationSubmit>[0]['simulation'],
      geodataId: props?.geodataId,
    });

    if (result.type === 'error_no_roads') {
      viewContext.notification.show(
        'Noise: no streets of the selected types were found at this site. Update street types or remove the Noise simulation.',
        'error',
        true,
        'toast'
      );
      return false;
    }

    if (result.type === 'warning_unreachable') {
      viewContext.notification.show(
        'OpenStreetMap is unavailable. The simulation will still be created, but we could not verify streets for Noise.',
        'warning',
        true,
        'toast'
      );
      return true;
    }

    return true;
  }, [props?.geodataId, simulation, viewContext.notification]);

  const notifyStorageLimitReached = useCallback(() => {
    viewContext.notification.show(STORAGE_LIMIT_REACHED_MESSAGE, 'error', true, 'toast');
  }, [viewContext.notification]);

  const view = props?.views?.[activeView];
  const getStepIdByIndex = (index: number) => props?.views?.[index]?.key || `step-${index}`;

  const isStepBlocked = (stepIndex: number) => {
    const stepId = getStepIdByIndex(stepIndex);
    return Boolean(invalidStepsById[stepId]);
  };

  const canNavigateToStep = (targetIndex: number) => {
    if (targetIndex <= activeView) return true;
    for (let i = activeView; i < targetIndex; i += 1) {
      if (isStepBlocked(i)) return false;
    }
    return true;
  };

  const canProceedCurrentStep =
    isReadOnly ||
    (!isSubmitting &&
      !isStepBlocked(activeView) &&
      isSimulationNameValid &&
      isSimulationNoiseSourcesValid &&
      isSimulationWindSimulationsValid);

  async function goTo(viewIndex: number) {
    if (isSubmitting) return;
    window.location.hash = String(viewIndex + 1);
    setActiveView(viewIndex);
  }

  const handleNext = async () => {
    if (isReadOnly) {
      goTo(activeView + 1);
      return;
    }

    if (!canProceedCurrentStep) return;

    if (view?.key === 'noise') {
      setIsSubmitting(true);
      try {
        const canProceedFromNoise = await checkNoiseRoadsBeforeSimulationSubmit();
        if (!canProceedFromNoise) {
          return;
        }
      } finally {
        setIsSubmitting(false);
      }
    }

    goTo(activeView + 1);
  };

  const remainingSimulationCount = toFiniteNumberOrNull(
    props?.projectData?.simulationsAvailableCount ?? currentProject?.simulationsAvailableCount
  );
  const requestedSimulationCount = getRequestedSimulationCount();
  const shouldBypassUsageLimits = (props?.countsTowardUsage ?? true) === false;
  const simulationRunLimitMessage = shouldBypassUsageLimits
    ? ''
    : getSimulationLimitMessage(remainingSimulationCount, requestedSimulationCount);
  const isRunDisabledByLimit = Boolean(simulationRunLimitMessage);

  const resetDependentForms = () => {
    clearGridFormData();
    clearGeodataFormData();
    clearSimulationFormData();
  };

  const handleSaveSimulation = async (shouldRun = false) => {
    if (isReadOnly || isSubmitting || hasInputFieldError) {
      return;
    }

    if (!isShadowSimulationDefined(simulation)) {
      setShadowDefinitionDialogOpen(true);
      return;
    }

    if (isStorageLimitReachedState && shouldRun) {
      notifyStorageLimitReached();
      return;
    }

    const canProceedFromNoise = await checkNoiseRoadsBeforeSimulationSubmit();
    if (!canProceedFromNoise) {
      return;
    }

    if (shouldRun) {
      const hasSimulationAvailability = await validateProjectEntityAvailability(getRequestedSimulationCount());
      if (!hasSimulationAvailability) {
        return;
      }
    }

    try {
      setIsSubmitting(true);
      const createdItem = await submitSimulation();
      if (!props?.preserveDraftState) {
        resetDependentForms();
      }
      setLastAddedItem(createdItem);

      if (shouldRun) {
        const newSimulations = { ...allSubSimulations };
        newSimulations[String(createdItem?.id)] = createdItem?.subSimulations ?? [];
        setAllSimulations(newSimulations);
        await handleRunSimulation(createdItem as unknown as SimulationNode, 'mixed');
        return;
      }

      navigate(`/projects/${props?.projectData?.id ?? currentProject?.id ?? ''}`);
    } catch (error) {
      console.error('Error saving simulation:', error);
      viewContext.notification.show(
        getApiErrorMessage(error, 'Unable to save simulation right now.'),
        'error',
        true,
        'toast'
      );
      setIsSubmitting(false);
    }
  };

  if (!props?.views?.length) return false;

  return (
    <Animate type="pop-center">
      <section
        className={`${Style.main} onboarding-container-section ${
          view?.fillAvailableSpace ? 'simulation-creation-flow--fill-available-space' : ''
        }`}
      >
        <CheckList
          color="white"
          hideCross
          interactive
          items={props.views.map((wizardView, index) => ({
            name: wizardView.name,
            checked: index === activeView,
            onClick: () => {
              if (isSubmitting) return;
              if (isReadOnly) {
                goTo(index);
                return;
              }
              if (hasInputFieldError) return;
              if (canNavigateToStep(index)) {
                goTo(index);
              }
            },
          }))}
        />

        {!view?.hideDefaultHeader && (
          <header className={Style.header}>
            <h2 className={Style.name}>{view?.title}</h2>
            <IconButton
              size="large"
              className={`${Style.exit} exit-icon`}
              onClick={() => {
                if (isSubmitting) return;
                if (isReadOnly) {
                  if (typeof props?.onExit === 'function') {
                    props.onExit();
                    return;
                  }
                  navigate(-1);
                  return;
                }
                setExitModalOpen(true);
              }}
              disabled={isSubmitting}
              style={!isReadOnly ? { visibility: 'hidden', pointerEvents: 'none' } : undefined}
              aria-hidden={!isReadOnly ? true : undefined}
              tabIndex={!isReadOnly ? -1 : undefined}
            >
              <CloseIcon />
            </IconButton>
          </header>
        )}

        {view?.customHeader ?? null}

        <article ref={articleRef} className={`${Style.article} ${isReadOnly ? 'onboarding-readonly' : ''}`}>
          {view?.component}
        </article>
      </section>

      <nav className="onboarding-nav onboarding-nav--fixed">
        <div className="onboarding-nav-left">
          {(activeView > 0 || typeof view?.onBack === 'function') && (
            <Button
              variant="contained"
              startIcon={<ChevronLeftIcon />}
              onClick={() => {
                if (typeof view?.onBack === 'function') {
                  view.onBack();
                  return;
                }

                goTo(activeView - 1);
              }}
              className={`${Style.prev} onboarding-prev-button`}
              disabled={isSubmitting || !canProceedCurrentStep}
            >
              Back
            </Button>
          )}
        </div>

        <div className="onboarding-nav-right">
          {!hideForwardNavigation && activeView < props.views.length - 1 && !view?.hideNextButton && (
            <Button
              variant="contained"
              endIcon={
                isSubmitting ? <CircularProgress size={18} thickness={5} color="inherit" /> : <ChevronRightIcon />
              }
              onClick={handleNext}
              className={`${Style.next} onboarding-next-button`}
              disabled={!canProceedCurrentStep}
            >
              Next
            </Button>
          )}

          {!hideForwardNavigation &&
            activeView === props.views.length - 1 &&
            (isReadOnly ? (
              <Button
                variant="contained"
                className="onboarding-finish-button"
                onClick={() => {
                  if (typeof props?.onExit === 'function') {
                    props.onExit();
                    return;
                  }
                  navigate(-1);
                }}
              >
                Close
              </Button>
            ) : (
              <>
                <Button
                  variant="contained"
                  startIcon={
                    isSubmitting ? <CircularProgress size={18} thickness={5} color="inherit" /> : <CheckCircle />
                  }
                  className="onboarding-finish-button"
                  onClick={() => handleSaveSimulation(false)}
                  disabled={isSubmitting || hasInputFieldError}
                >
                  Finish
                </Button>

                {isGeodataGathered && (
                  <Tooltip
                    title={
                      isStorageLimitReachedState
                        ? STORAGE_LIMIT_REACHED_MESSAGE
                        : isRunDisabledByLimit
                          ? simulationRunLimitMessage
                          : ''
                    }
                  >
                    <span style={{ display: 'inline-flex' }}>
                      <Button
                        variant="contained"
                        startIcon={
                          isSubmitting ? <CircularProgress size={18} thickness={5} color="inherit" /> : <PlayCircle />
                        }
                        className="onboarding-finish-button"
                        onClick={() => handleSaveSimulation(true)}
                        disabled={
                          isStorageLimitReachedState || isRunDisabledByLimit || isSubmitting || hasInputFieldError
                        }
                      >
                        Run
                      </Button>
                    </span>
                  </Tooltip>
                )}
              </>
            ))}
        </div>
      </nav>

      <ExitDialog
        open={exitModalOpen}
        onClose={() => setExitModalOpen(false)}
        mode="confirm"
        onCancel={() => {
          if (isSubmitting) return;
          setExitModalOpen(false);
        }}
        onConfirm={() => {
          if (isSubmitting) return;
          setExitModalOpen(false);
          if (typeof props?.onExit === 'function') {
            props.onExit();
            return;
          }
          const projectId = props?.projectData?.id ?? currentProject?.id;
          if (!projectId) {
            navigate('/dashboard');
            return;
          }
          navigate(`/projects/${projectId}`);
        }}
        isBusy={isSubmitting}
      />
      <ExitDialog
        open={shadowDefinitionDialogOpen}
        onClose={() => setShadowDefinitionDialogOpen(false)}
        mode="confirm"
        title={SHADOW_NOT_DEFINED_MESSAGE}
        width={460}
        cancelLabel="Cancel"
        confirmLabel="Set Shadow"
        onCancel={() => setShadowDefinitionDialogOpen(false)}
        onConfirm={() => {
          const shadowViewIndex = props.views.findIndex((wizardView) => wizardView.key === 'shadow');
          if (shadowViewIndex < 0) return;
          setShadowDefinitionDialogOpen(false);
          goTo(shadowViewIndex);
        }}
      />
    </Animate>
  );
}

export default SimulationCreationFlow;
